import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Chip,
  IconButton,
  TextField,
  Stack,
  Button,
  Divider,
} from '@mui/material';
import { CheckCircle, Cancel, ExpandMore, ExpandLess, Edit as EditIcon, Save as SaveIcon, Search as SearchIcon, CalendarMonth as CalendarMonthIcon, AccessTime as AccessTimeIcon, Person as PersonIcon } from '@mui/icons-material';
import { styled } from '@mui/system';
import { alpha } from '@mui/material/styles';
import axiosInstance from '../../api/axiosConfig';
import NoRequestsImage from '../../assets/no-results.png';
import PageContainer from '../common/PageContainer';
import AdminPageHero from '../common/AdminPageHero';
import AdminPanelCard from '../common/AdminPanelCard';
import EmptyState from '../common/EmptyState';

// Styled Components
const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 700,
  minWidth: 120,
  minHeight: 40,
  padding: theme.spacing(1, 2),
  marginRight: theme.spacing(1),
  borderRadius: 6,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
  },
  '&:not(.Mui-selected)': {
    color: theme.palette.text.secondary,
  },
}));

const TabBarCard = styled(AdminPanelCard)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: 6,
  background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
}));

const ListCard = styled(AdminPanelCard)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: 6,
  background: 'linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)',
}));

const SummaryStrip = styled(AdminPanelCard)(({ theme }) => ({
  borderRadius: 6,
  padding: theme.spacing(2),
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, rgba(255,255,255,0.96))`,
}));

const RequestCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 6,
  backgroundColor: theme.palette.common.white,
  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
  marginBottom: theme.spacing(1.4),
  boxShadow: 'none',
  '&:not(:last-of-type)': {
    marginBottom: theme.spacing(1.4),
  },
}));

const EmployeeLeaveRequests = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [expandedRows, setExpandedRows] = useState({});
  const [editMode, setEditMode] = useState({});
  const [editedReason, setEditedReason] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [searchTerm, setSearchTerm] = useState('');

  const statusMap = { 0: 'PENDING', 1: 'APPROVED', 2: 'REJECTED' };
  const tabConfig = ['Pending', 'Approved', 'Rejected'];
  const currentStatus = statusMap[selectedTab];
  const schoolId = localStorage.getItem('schoolId');

  useEffect(() => {
    const fetchLeaveRequests = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await axiosInstance.get('/api/leave-requests/employee', {
          params: { status: currentStatus, schoolId },
        });
        // Mock casualLeavesRemaining; replace with actual API data if available
        const enrichedData = (response.data || []).map(req => ({
          ...req,
          casualLeavesRemaining: Math.max(0, 10 - (req.leaveType === 'Casual' ? 1 : 0)), // Simplified logic
        }));
        setLeaveRequests(enrichedData);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch leave requests.');
        setLeaveRequests([]);
      } finally {
        setLoading(false);
      }
    };
    fetchLeaveRequests();
  }, [currentStatus, schoolId]);

  const filteredRequests = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return leaveRequests;
    }

    return leaveRequests.filter((request) => {
      const haystack = [
        request.employeeName,
        request.leaveType,
        request.reason,
        request.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [leaveRequests, searchTerm]);

  const statusSummary = useMemo(() => {
    const totalDays = filteredRequests.reduce((sum, request) => {
      if (!request.startDate || !request.endDate) {
        return sum;
      }
      const start = new Date(request.startDate);
      const end = new Date(request.endDate);
      const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      return sum + Math.max(1, diff);
    }, 0);

    return [
      { label: 'Visible Requests', value: filteredRequests.length },
      { label: 'Leave Days', value: totalDays },
      { label: 'People Impacted', value: new Set(filteredRequests.map((req) => req.employeeName)).size },
    ];
  }, [filteredRequests]);

  const handleApprove = async requestId => {
    setActionLoading(true);
    try {
      await axiosInstance.put(`/api/leave-requests/approve/employee/${requestId}`);
      setLeaveRequests(prev =>
        prev.map(req => 
          req.requestId === requestId 
            ? { ...req, status: 'APPROVED', casualLeavesRemaining: req.leaveType === 'Casual' ? Math.max(0, req.casualLeavesRemaining - 1) : req.casualLeavesRemaining }
            : req
        )
      );
      showSnackbar('Approved successfully.', 'success');
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to approve.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async requestId => {
    setActionLoading(true);
    try {
      await axiosInstance.put(`/api/leave-requests/reject/employee/${requestId}`);
      setLeaveRequests(prev =>
        prev.map(req => (req.requestId === requestId ? { ...req, status: 'REJECTED' } : req))
      );
      showSnackbar('Rejected successfully.', 'success');
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to reject.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveReason = async requestId => {
    const newReason = editedReason[requestId] || '';
    setActionLoading(true);
    try {
      await axiosInstance.put(`/api/leave-requests/employee/${requestId}`, { reason: newReason });
      setLeaveRequests(prev =>
        prev.map(req => (req.requestId === requestId ? { ...req, reason: newReason } : req))
      );
      setEditMode(prev => ({ ...prev, [requestId]: false }));
      showSnackbar('Reason updated successfully.', 'success');
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to update reason.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setExpandedRows({});
    setEditMode({});
  };

  const toggleRow = id => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleEdit = (id, currentReason) => {
    setEditMode(prev => ({ ...prev, [id]: !prev[id] }));
    setEditedReason(prev => ({ ...prev, [id]: currentReason }));
  };

  const renderRequestCard = request => {
    const isExpanded = expandedRows[request.requestId] || false;
    const isEditing = editMode[request.requestId] || false;
    const showActions = currentStatus === 'PENDING';

    const leaveDays =
      request.startDate && request.endDate
        ? Math.max(
            1,
            Math.round(
              (new Date(request.endDate).getTime() - new Date(request.startDate).getTime()) /
                (1000 * 60 * 60 * 24)
            ) + 1
          )
        : 1;

    return (
      <RequestCard key={request.requestId}>
        <Stack spacing={1.5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Stack spacing={1.1} sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={800}>
                {request.employeeName}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip icon={<PersonIcon />} label={request.leaveType} size="small" sx={{ fontWeight: 700 }} />
                <Chip
                  icon={<CalendarMonthIcon />}
                  label={`${new Date(request.startDate).toLocaleDateString()} - ${new Date(request.endDate).toLocaleDateString()}`}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
                <Chip icon={<AccessTimeIcon />} label={`${leaveDays} day${leaveDays > 1 ? 's' : ''}`} size="small" sx={{ fontWeight: 700 }} />
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap justifyContent="flex-end">
              <Chip
                label={request.status}
                color={request.status === 'APPROVED' ? 'success' : request.status === 'REJECTED' ? 'error' : 'warning'}
                size="small"
                variant="outlined"
              />
              {request.reason && (
                <Button
                  size="small"
                  onClick={() => toggleRow(request.requestId)}
                  endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
                  sx={{ fontWeight: 700, textTransform: 'none' }}
                >
                  {isExpanded ? 'Hide note' : 'Show note'}
                </Button>
              )}
            </Stack>
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Casual leaves remaining: <strong>{request.casualLeavesRemaining}</strong>
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {showActions && (
                <>
                  <Button
                    color="success"
                    variant="contained"
                    onClick={() => handleApprove(request.requestId)}
                    disabled={actionLoading}
                    startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
                    sx={{ borderRadius: 6, fontWeight: 700, textTransform: 'none' }}
                  >
                    Approve
                  </Button>
                  <Button
                    color="error"
                    variant="outlined"
                    onClick={() => handleReject(request.requestId)}
                    disabled={actionLoading}
                    startIcon={actionLoading ? <CircularProgress size={16} color="inherit" /> : <Cancel />}
                    sx={{ borderRadius: 6, fontWeight: 700, textTransform: 'none' }}
                  >
                    Reject
                  </Button>
                </>
              )}
            </Box>
          </Stack>
        </Stack>
        {request.reason && isExpanded && (
          <Box sx={{ mt: 1.8, p: 1.5, bgcolor: alpha('#4F46E5', 0.06), borderRadius: 2.5 }}>
            {isEditing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label="Reason"
                  value={editedReason[request.requestId] || request.reason}
                  onChange={e =>
                    setEditedReason(prev => ({ ...prev, [request.requestId]: e.target.value }))
                  }
                  fullWidth
                  size="small"
                  variant="outlined"
                />
                <IconButton
                  color="primary"
                  onClick={() => handleSaveReason(request.requestId)}
                  disabled={actionLoading}
                >
                  {actionLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Reason:</strong> {request.reason}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => toggleEdit(request.requestId, request.reason)}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            )}
          </Box>
        )}
      </RequestCard>
    );
  };

  return (
      <PageContainer sx={{ width: '100%' }}>
        <AdminPageHero
          title="Leave Management"
          subtitle="Review and process pending, approved, and rejected leave requests."
          metrics={[
            { label: 'Visible', value: leaveRequests.length },
            { label: 'Pending', value: leaveRequests.filter((req) => req.status === 'PENDING').length },
            { label: 'Approved', value: leaveRequests.filter((req) => req.status === 'APPROVED').length },
            { label: 'Rejected', value: leaveRequests.filter((req) => req.status === 'REJECTED').length },
          ]}
        />

        <TabBarCard contentSx={{ p: 0 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons={false}
            sx={{ px: 0.5 }}
          >
            {tabConfig.map((label) => (
              <StyledTab key={label} label={label} />
            ))}
          </Tabs>
        </TabBarCard>

        <SummaryStrip>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {tabConfig[selectedTab]} queue
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Keep the review list short, searchable, and easy to act on.
                </Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Search employee, leave type, or reason"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                sx={{ minWidth: { xs: '100%', lg: 320 } }}
                InputProps={{
                  startAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, color: 'text.secondary' }}>
                      <SearchIcon fontSize="small" />
                    </Box>
                  ),
                }}
              />
            </Stack>
            <Divider />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              {statusSummary.map((item) => (
                <Box
                  key={item.label}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRadius: 1.5,
                    p: 1.5,
                    bgcolor: alpha('#ffffff', 0.8),
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" sx={{ textTransform: 'uppercase', fontWeight: 800, color: 'text.secondary', letterSpacing: 0.8 }}>
                    {item.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 900, mt: 0.5 }}>
                    {item.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Stack>
        </SummaryStrip>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <AdminPanelCard>
            <Typography variant="body1" color="error" sx={{ textAlign: 'center', py: 4 }}>
              {error}
            </Typography>
          </AdminPanelCard>
        ) : filteredRequests.length === 0 ? (
          <EmptyState
            title={searchTerm ? 'No matching leave requests' : `No ${currentStatus.toLowerCase()} leave requests`}
            description={searchTerm ? 'Try a different search term or switch the request status tab.' : 'There are no requests in this status right now.'}
            icon={<img src={NoRequestsImage} alt="No Requests" style={{ width: '150px', opacity: 0.8 }} />}
          />
        ) : (
          <ListCard
            title="Leave Requests"
            subtitle={`Review ${currentStatus.toLowerCase()} employee leave applications and update decisions quickly.`}
          >
            {filteredRequests.map(renderRequestCard)}
          </ListCard>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </PageContainer>
  );
};

export default EmployeeLeaveRequests;
